import { chains } from '@shared/src/config/chains';
import { createPublicClient, http, Chain, Hex, TypedDataDomain } from 'viem';

export async function poll<T>(callback: () => Promise<T>, waitTime: number, maxAttempts: number) {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const result = await callback();
      return result;
    } catch (e) {
      if (i === maxAttempts - 1) {
        throw e;
      }
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }
}

export const calculateAmount = (value: string, price: number, isReverse: boolean = false) => {
  const numValue = parseFloat(value || '0');
  const numPrice = price || 1;

  if (isReverse) {
    // Calculate input amount from output amount (multiply)
    return numValue ? (numValue * numPrice).toFixed(2) : '';
  } else {
    // Calculate output amount from input amount (divide)
    return numValue ? (numValue / numPrice).toFixed(2) : '';
  }
};

type AnyFunction = (...args: unknown[]) => Promise<unknown>;

export function withFallback<
  TFirst extends AnyFunction,
  TFallback extends (e: unknown) => Promise<unknown>,
>(first: TFirst, fallbackFn: TFallback) {
  try {
    const result = first();
    if (result instanceof Promise) {
      return result.catch((e: unknown) => fallbackFn(e));
    }
    return result;
  } catch (e) {
    return fallbackFn(e);
  }
}

/**
 * Get error message from various error types
 */
export const getErrorMessage = (error: any): string => {
  if (typeof error === 'string') return error;

  if (error?.message) return error.message;

  if (error?.reason) return error.reason;

  if (error?.data?.message) return error.data.message;

  if (typeof error?.toString === 'function') {
    const errorString = error.toString();
    if (errorString !== '[object Object]') return errorString;
  }

  return 'An unknown error occurrexd';
};

export const swap = { buy: 'sell', sell: 'buy' } as const;

/**
 * Get trade type and merchant status based on ad type and merchant address
 */
export const getTradeType = (
  adType: 'buy' | 'sell',
  merchantAddress: string,
  userAddress: `0x${string}`,
): { tradeType: 'buy' | 'sell'; isMerchant: boolean } => {
  const isMerchant = userAddress?.toLowerCase() === merchantAddress.toLowerCase();
  const tradeType = isMerchant ? swap[adType] : adType;
  return { tradeType, isMerchant };
};

/**
 * Compare two semantic version strings (e.g. "1.2.3", "1.2.3-alpha.1").
 * Implements SemVer precedence rules: https://semver.org/#spec-item-11
 * Returns:
 *  -1 if a < b
 *   0 if a == b
 *   1 if a > b
 */
export function compareSemver(a: string, b: string): number {
  if (a === b) return 0;

  // Split build metadata (ignored for precedence)
  const [coreA, buildA] = a.split('+'); // buildA unused but kept for clarity
  const [coreB, buildB] = b.split('+');

  const [mainA, preA] = coreA.split('-');
  const [mainB, preB] = coreB.split('-');

  const parseMain = (v: string): [number, number, number] => {
    const parts = v.split('.').map(n => Number(n) || 0);
    while (parts.length < 3) parts.push(0);
    return [parts[0], parts[1], parts[2]];
  };
  const [majA, minA, patA] = parseMain(mainA);
  const [majB, minB, patB] = parseMain(mainB);

  if (majA !== majB) return majA > majB ? 1 : -1;
  if (minA !== minB) return minA > minB ? 1 : -1;
  if (patA !== patB) return patA > patB ? 1 : -1;

  // If main versions equal, handle prerelease: absence of prerelease > presence
  if (preA === undefined && preB === undefined) return 0;
  if (preA === undefined) return 1; // a is a release, b is prerelease
  if (preB === undefined) return -1; // b is a release, a is prerelease

  const identA = preA.split('.');
  const identB = preB.split('.');
  const len = Math.max(identA.length, identB.length);

  for (let i = 0; i < len; i++) {
    const ia = identA[i];
    const ib = identB[i];
    if (ia === undefined) return -1; // a ran out first -> lower
    if (ib === undefined) return 1; // b ran out first -> lower
    if (ia === ib) continue;
    const numA = ia.match(/^\d+$/) ? Number(ia) : NaN;
    const numB = ib.match(/^\d+$/) ? Number(ib) : NaN;
    const aIsNum = !isNaN(numA);
    const bIsNum = !isNaN(numB);
    if (aIsNum && bIsNum) {
      if (numA !== numB) return numA > numB ? 1 : -1;
    } else if (aIsNum && !bIsNum) {
      return -1; // Numeric identifiers have lower precedence than non-numeric
    } else if (!aIsNum && bIsNum) {
      return 1;
    } else {
      return ia > ib ? 1 : -1; // ASCII sort
    }
  }
  return 0;
}

export async function verifyTypedDataSig({
  address,
  domain,
  types,
  primaryType,
  message,
  signature,
  chain,
}: {
  address: Hex;
  domain: TypedDataDomain;
  types: Record<string, any>;
  primaryType: string;
  message: Record<string, any>;
  signature: Hex;
  chain: Chain;
}): Promise<boolean> {
  // Create a public client for the specific chain
  const publicClient = createPublicClient({
    chain: chain,
    transport: http(chain.rpcUrls.default.http[0]),
  });

  const isValid = await publicClient.verifyTypedData({
    address,
    domain,
    types,
    primaryType,
    message,
    signature,
  });

  if (isValid) {
    return isValid;
  } else {
    throw new Error('Invalid Siwe...');
  }
}
